import { useEffect, useMemo } from 'react';
import {
	SubscribeToEventsParams,
	disposeSubscription,
	selectEventsByRef,
	selectIsFetchingByRef,
	selectIsInitializedByRef,
	selectIsLoadingByRef,
	subscribeToEvents
} from './sportsbook-event-subscription-duck';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store/types';
import { State } from '../../sportsbook-duck';

type Params = SubscribeToEventsParams & {
	isEnabled?: boolean;
};

/**
 * Subscribe to events and return denormalized events data.
 * @param ref - uniq reference use for subscription.
 * @param filters - filters to apply to subscription
 * @param isEnabled - flag to indicate if subscription enabled.
 * @param orderBy - event sort order.
 * @param pageSize - the maximum number of events to return.
 */
const useEventSubscription = ({ ref, filters, orderBy, pageSize, isEnabled = true }: Params) => {
	const dispatch: AppDispatch = useDispatch();
	const events = useSelector((state: State) => selectEventsByRef(state, ref));
	const isLoading = useSelector((state: State) => selectIsLoadingByRef(state, ref));
	const isFetching = useSelector((state: State) => selectIsFetchingByRef(state, ref));
	const isInitialized = useSelector((state: State) => selectIsInitializedByRef(state, ref));

	// Sort markets based on filters.marketTypeFilters
	const eventsWithSortedMarkets = useMemo(() => {
		if (!filters?.marketTypeFilters || filters.marketTypeFilters?.length === 0) {
			return events;
		}

		return events.map(event => {
			const sortedMarkets = [...event.markets].sort((a, b) => {
				const aIndex =
					filters.marketTypeFilters?.findIndex(filter => filter.marketTypeIds.includes(a.marketType.id)) ?? 0;
				const bIndex =
					filters.marketTypeFilters?.findIndex(filter => filter.marketTypeIds.includes(b.marketType.id)) ?? 0;
				return aIndex - bIndex;
			});

			// Only clone if order changes
			if (event.markets.every((market, i) => market.id === sortedMarkets.at(i)?.id)) {
				return event; // Reuse the same reference if order didn't change
			}

			return { ...event, markets: sortedMarkets };
		});
	}, [filters?.marketTypeFilters, events]);

	useEffect(() => {
		if (!filters) {
			return;
		}

		// If subscription is disabled, dispose subscription.
		if (!isEnabled) {
			dispatch(disposeSubscription(ref));
			return;
		}

		dispatch(subscribeToEvents({ ref, filters, orderBy, pageSize }));

		return () => {
			dispatch(disposeSubscription(ref));
		};
	}, [dispatch, isEnabled, filters, ref, orderBy, pageSize]);

	return { events: eventsWithSortedMarkets, isLoading, isFetching, isInitialized };
};

export default useEventSubscription;
